import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Group, Skeleton, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import BestPriceLabel from '@/components/common/BestPriceLabel';
import { useSellPriceCandidates, useTransportSurcharges } from '@/hooks/useBestOffers';
import { recomputeSellPriceRows } from '@/lib/sellSurcharge';
import type { RecommendedSellPrice } from '@/types/bestPrice';
import classes from './SellPriceOrientation.module.css';

interface SellPriceOrientationProps {
  /** Мой базис (точка погрузки). */
  readonly address?: string;
  /** Культура с классом («Пшеница 4 класс»). */
  readonly goodsType?: string | null;
  /** Качество (propsSetId). */
  readonly propsSetId?: string | number | null;
  /** Наличие весов на базисе погрузки (влияет на надбавку к перевозке). */
  readonly hasScales: boolean;
  /** Наличие тральщика. */
  readonly hasTrawl: boolean;
  /** Наличие самосвала. */
  readonly hasTrailer: boolean;
  /** Продать по выбранной цене: бросаем создание оффера, уходим на экран встречной продажи. */
  readonly onSell: (row: RecommendedSellPrice) => void;
}

// Встречный экран продажи (free-mode) поддерживает только эти типы оплаты (SellPrepaymentType).
// Прочие (20%/90%) — на будущее; пока их не показываем, чтобы «Продать по ней» не уводило с подменой условий.
const COUNTER_PREPAYMENTS: ReadonlySet<string> = new Set(['PERCENT_0', 'PERCENT_100']);

function prepaymentLabel(t: (key: string, opts?: Record<string, unknown>) => string, type: string): string {
  const match = /PERCENT_(\d+)/.exec(type);
  const percent = match ? Number(match[1]) : null;
  if (percent === 0) {
    return t('app.sell.priceHint.noPrepayment');
  }
  if (percent != null) {
    return t('app.sell.priceHint.prepayment', { percent });
  }
  return type;
}

// Ориентир «по какой цене можно продать» в форме создания оффера (только внебиржевое). Рекомендованные
// FCA-цены по типам оплаты: самая выгодная (первая) — залитая янтарная, остальные — мягкие янтарные
// (единый язык «лучшей цены», сторона продажи). Клик по строке → продать по ней (на экран встречного).
export default function SellPriceOrientation({
  address,
  goodsType,
  propsSetId,
  hasScales,
  hasTrawl,
  hasTrailer,
  onSell,
}: SellPriceOrientationProps) {
  const { t } = useTranslation();
  const ready = !!address && !!goodsType && propsSetId != null && propsSetId !== '';
  const { data, isLoading } = useSellPriceCandidates({ address, goodsType, propsSetId });
  const { data: surcharges } = useTransportSurcharges();

  // Пересчёт на клиенте: из кандидатов (цена при всём оборудовании ВКЛ + дистанция) вычитаем надбавки за
  // отключённое оборудование и берём лучший порт по каждому типу оплаты. Мгновенно при переключении —
  // без повторного запроса (кэш кандидатов и ставок не зависит от оборудования).
  const rows = useMemo(
    () =>
      recomputeSellPriceRows(data, { hasScales, hasTrawl, hasTrailer }, surcharges).filter((row) =>
        COUNTER_PREPAYMENTS.has(row.prepaymentType),
      ),
    [data, surcharges, hasScales, hasTrawl, hasTrailer],
  );

  // Обязательные параметры не заполнены — панель не показываем (без шапки-пустышки).
  if (!ready) {
    return null;
  }

  return (
    // Выделенный блок: янтарная обводка + заголовок белым по янтарной плашке во всю ширину (сторона продажи).
    // Плашка ask.7 (не ask.6): белый текст даёт контраст ≈5.2:1 — уровень AA для мелкого текста.
    <Card withBorder padding={0} radius="md" style={{ overflow: 'hidden', borderColor: 'var(--mantine-color-ask-3)' }}>
      <Stack gap={0}>
        <Box bg="ask.7" px="md" py="xs">
          <Text fw={600} size="sm" c="white">
            {t('app.sell.priceHint.title')}
          </Text>
        </Box>

        {isLoading ? (
          <Stack gap="xs" p="md">
            <Skeleton h={30} />
            <Skeleton h={30} />
          </Stack>
        ) : rows.length > 0 ? (
          <Stack gap={0} pt="xs" pb="xs">
            {rows.map((row, index) => (
              <UnstyledButton
                key={row.prepaymentType}
                className={classes.row}
                onClick={() => onSell(row)}
                aria-label={`${prepaymentLabel(t, row.prepaymentType)} — ${row.price}`}
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text fw={500} size="sm" c="brand.9" style={{ minWidth: 0 }}>
                    {prepaymentLabel(t, row.prepaymentType)}
                  </Text>
                  <Group gap="sm" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                    <Stack gap={0} align="flex-end">
                      {/* Самая выгодная (первая) — gain (залитая янтарная); остальные — plain (мягкая янтарная). */}
                      <BestPriceLabel price={row.price} action="sell" tone={index === 0 ? 'gain' : 'plain'} />
                      {row.buyerAddressName ? (
                        <Text size="xs" c="gray.7">
                          {row.buyerAddressName}
                        </Text>
                      ) : null}
                    </Stack>
                    <IconChevronRight size={16} color="var(--mantine-color-gray-5)" />
                  </Group>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="gray.7" px="md" pb="md" pt="xs">
            {t('app.sell.priceHint.empty')}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
